import React, {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {Modal, ModalBody, ModalFooter, ModalHeader} from 'reactstrap';

import {AccountActionsConfiguration} from '../../business_units/accounts/AccountActionsConfiguration';
import {ANALYTICS_CHANNEL, CHANNEL_INFO_BY_CHANNEL} from '../../channels/channels';
import {mergeActionCounts} from '../campaignsPreview';
import {fetchAdAccountActionCounts} from '../lineWizardApi';

/**
 * «Eventos de conversión» de una cuenta (mockup `#/mano/conversiones`).
 *
 * Reusa el selector del clásico (`AccountActionsConfiguration` → `CategorizedActionsSelect`) y no un
 * picker nuevo: el mockup pide conservar justamente lo mejor de ese selector —los volúmenes 1D / 7D /
 * 30D, la marca «Popular» y las categorías—, que es lo que le permite a alguien no técnico reconocer
 * el evento correcto. Lo nuevo es el marco: un modal propio, que confirma con «Guardar eventos».
 *
 * GA4 trae sus conteos aparte (`actions/counts`): sin pedirlos, sus eventos eran una lista pelada,
 * sin badges, sin «Con datos» y sin la frase de cobertura (recorrido de fede, 01/10, punto 13). Se
 * piden al abrir y se mezclan cuando llegan; si fallan, la lista sigue igual que antes.
 */
export function ConversionEventsModal({config, onSave, onClose}) {
    const {t} = useTranslation();
    const [selected, setSelected] = useState(config.selected_actions || []);
    const [allActions, setAllActions] = useState(config.all_actions);
    // El selector decide en su constructor si arranca con «Con datos» prendido, mirando si los eventos
    // traen conteos: con los de GA4 llegando después, arrancaba apagado (en Google, prendido). Al
    // llegar, se vuelve a montar con su `key` (review C-rec-fe-linea-07). Lo elegido vive acá y no se
    // pierde.
    const [countsMerged, setCountsMerged] = useState(false);
    const info = CHANNEL_INFO_BY_CHANNEL[config.channel] || {};

    useEffect(() => {
        if (config.channel !== ANALYTICS_CHANNEL || !config.ad_account_id || !config.all_actions) return undefined;
        let cancelled = false;
        fetchAdAccountActionCounts(config.ad_account_id).then(response => {
            if (cancelled || response?.status !== 'OK' || !response.data) return;
            setAllActions(current => mergeActionCounts(current, response.data));
            setCountsMerged(true);
        });
        return () => {
            cancelled = true;
        };
    }, [config.channel, config.ad_account_id, config.all_actions]);

    return (
        <Modal isOpen toggle={onClose} className="lw-modal">
            <ModalHeader toggle={onClose}>
                {t('line_wizard.conversions_modal.title', {account: config.name, interpolation: {escapeValue: false}})}
            </ModalHeader>
            <ModalBody>
                <div className="modal-section">
                    <p className="lw-modal__sub">{t('line_wizard.conversions_modal.subtitle')}</p>
                    {allActions === null || allActions === undefined ? (
                        <p className="lw-modal__help">{t('line_wizard.conversions_modal.not_synced')}</p>
                    ) : (
                        <AccountActionsConfiguration
                            key={countsMerged ? 'with-counts' : 'without-counts'}
                            selectedActions={selected}
                            allActions={allActions}
                            onChange={actions => setSelected(actions || [])}
                            hasIncludeAllConversionsSetting={info.hasIncludeAllConversionsSetting}
                            useAllPrimaryConversions={selected.length === 0}
                            channel={config.channel}
                        />
                    )}
                </div>
            </ModalBody>
            <ModalFooter>
                <button type="button" className="btn -alt -negative" onClick={onClose}>{t('line_wizard.cancel')}</button>
                <button type="button" className="btn -alt" onClick={() => onSave({selected_actions: selected})}>
                    {t('line_wizard.conversions_modal.save')}
                </button>
            </ModalFooter>
        </Modal>
    );
}
